/**
 * EPIC APT strings. Arabic is the source language (CONVENTIONS §8).
 *
 * Copy is transcribed from `design/MedOSOps.dc.html` (appointments, week) and
 * `design/MedOSAuthPublic.dc.html` (publicBooking, publicConfirm) rather than
 * invented, so the screens read the way the design says they read.
 */
export const appointmentsAr: Record<string, string> = {
  // --- List and day view ---------------------------------------------------
  'apt.title': 'المواعيد',
  'apt.subtitle': '{count} موعداً · {branch}',
  'apt.viewList': 'قائمة',
  'apt.viewWeek': 'أسبوع',
  'apt.print': 'طباعة الجدول',
  'apt.export': 'تصدير',
  'apt.new': '+ موعد جديد',
  'apt.today': 'اليوم',
  'apt.filter.waiting': 'في الانتظار',
  'apt.filter.noShow': 'لم يحضر',
  'apt.filter.public': 'حجز عام',
  'apt.filter.doctor': 'الطبيب',
  'apt.filter.service': 'الخدمة',
  'apt.filter.branch': 'الفرع',
  'apt.filter.all': 'الكل',
  'apt.filter.status': 'الحالة',
  'apt.filter.source': 'المصدر',
  'apt.filter.clear': 'مسح المرشحات',
  'apt.search.placeholder': 'ابحث بالاسم أو رقم الملف أو الجوال…',

  // --- Table ---------------------------------------------------------------
  'apt.col.time': 'الوقت',
  'apt.col.patient': 'المريض',
  'apt.col.doctor': 'الطبيب',
  'apt.col.service': 'الخدمة',
  'apt.col.source': 'المصدر',
  'apt.col.status': 'الحالة',
  'apt.col.action': 'إجراء',
  'apt.count': '{shown} من {total} موعداً',
  'apt.prev': 'السابق',
  'apt.next': 'التالي',

  // --- Quick actions -------------------------------------------------------
  'apt.action.confirm': 'تأكيد',
  'apt.action.arrive': 'تسجيل وصول',
  'apt.action.start': 'بدء الكشف',
  'apt.action.ready': 'جاهز للمحاسبة',
  'apt.action.checkout': 'محاسبة',
  'apt.action.view': 'عرض',
  'apt.action.reschedule': 'إعادة جدولة',
  'apt.action.cancel': 'إلغاء',
  'apt.action.noShow': 'لم يحضر',
  'apt.action.none': '—',

  // --- Sources -------------------------------------------------------------
  'apt.source.STAFF': 'استقبال',
  'apt.source.PUBLIC': 'حجز عام',
  'apt.source.WALK_IN': 'بلا موعد',
  'apt.source.FOLLOW_UP': 'متابعة',
  'apt.source.IMPORT': 'استيراد',

  // --- Empty and error states ---------------------------------------------
  'apt.empty.title': 'لا مواعيد في هذا اليوم',
  'apt.empty.body': 'غيّر التاريخ أو المرشحات، أو احجز موعداً جديداً.',
  'apt.empty.filtered.title': 'لا نتائج مطابقة',
  'apt.empty.filtered.body': 'جرّب مرشحات أوسع أو امسحها كلها.',

  // --- Booking drawer ------------------------------------------------------
  'apt.book.title': 'حجز موعد جديد',
  'apt.book.patient': 'المريض',
  'apt.book.searchPatient': 'ابحث بالاسم أو رقم الملف أو الجوال…',
  'apt.book.quickCreate': '+ إنشاء مريض جديد سريعاً',
  'apt.book.quickCreateTitle': 'مريض جديد (مؤقت)',
  'apt.book.contactName': 'الاسم الكامل',
  'apt.book.contactPhone': 'رقم الجوال',
  'apt.book.contactNote': 'يُسجَّل كجهة اتصال مؤقتة، ولا يُنشأ ملف مريض إلا عند تسجيل الوصول.',
  'apt.book.doctor': 'الطبيب',
  'apt.book.type': 'نوع الموعد',
  'apt.book.service': 'الخدمة',
  'apt.book.serviceNone': 'بدون خدمة محددة',
  'apt.book.date': 'التاريخ',
  'apt.book.slots': 'الوقت المتاح',
  'apt.book.noSlots': 'لا أوقات متاحة في هذا اليوم',
  'apt.book.reason': 'سبب الزيارة',
  'apt.book.reasonPlaceholder': 'اختياري…',
  'apt.book.feePolicy': 'سياسة الرسوم',
  'apt.book.submit': 'تأكيد الحجز',
  'apt.book.cancel': 'إلغاء',
  'apt.book.asRequest': 'حفظ كطلب بانتظار التأكيد',
  'apt.book.selectPatient': 'اختر مريضاً أو أنشئ جهة اتصال أولاً',
  'apt.book.selectSlot': 'اختر وقتاً متاحاً',
  'apt.book.conflict': 'حُجز هذا الوقت للتو. اختر وقتاً آخر.',
  'apt.book.balanceWarning': 'على المريض رصيد مستحق {amount}',
  'apt.book.clinicalWarning': 'لهذا المريض تنبيهات سريرية مسجلة',
  'apt.book.sameDayWarning': 'للمريض موعد آخر في نفس اليوم — تعارض المريض تحذيري فقط.',

  // --- Fee policies --------------------------------------------------------
  'apt.fee.STANDARD': 'كشف',
  'apt.fee.FOLLOW_UP': 'متابعة',
  'apt.fee.EXEMPT': 'معفى',
  'apt.fee.FREE': 'مجاني',
  'apt.fee.SERVICE_PRICE': 'سعر الخدمة',

  // --- Appointment types ---------------------------------------------------
  'apt.type.FIRST_VISIT': 'كشف أول',
  'apt.type.FOLLOW_UP': 'متابعة',
  'apt.type.PROCEDURE': 'إجراء',
  'apt.type.CONSULTATION': 'استشارة',
  'apt.type.EMERGENCY': 'طارئ',

  // --- Cancel / no-show dialogs -------------------------------------------
  'apt.cancel.title': 'إلغاء الموعد',
  'apt.cancel.reason': 'سبب الإلغاء',
  'apt.cancel.reasonRequired': 'السبب مطلوب ويُسجَّل في سجل الموعد.',
  'apt.cancel.submit': 'تأكيد الإلغاء',
  'apt.noShow.title': 'تسجيل عدم حضور',
  'apt.noShow.reason': 'سبب عدم الحضور',
  'apt.noShow.submit': 'تأكيد',
  'apt.reschedule.title': 'إعادة جدولة الموعد',
  'apt.reschedule.current': 'الموعد الحالي',
  'apt.reschedule.submit': 'نقل الموعد',
  'apt.dialog.close': 'إغلاق',

  // --- Week calendar -------------------------------------------------------
  'apt.week.title': 'التقويم الأسبوعي',
  'apt.week.prev': 'الأسبوع السابق',
  'apt.week.next': 'الأسبوع التالي',
  'apt.week.legend.confirmed': 'موعد مؤكد',
  'apt.week.legend.requested': 'بانتظار التأكيد',
  'apt.week.legend.closed': 'استراحة / خارج الدوام',
  'apt.week.legend.leave': 'إجازة',
  'apt.week.pickDoctor': 'اختر طبيباً لعرض التقويم',

  // --- Public booking (mobile) --------------------------------------------
  'apt.public.step.branch': 'اختر الفرع والطبيب',
  'apt.public.step.service': 'اختر الخدمة',
  'apt.public.step.time': 'اختر الوقت المناسب',
  'apt.public.step.details': 'بياناتك',
  'apt.public.branch': 'الفرع',
  'apt.public.doctor': 'الطبيب',
  'apt.public.service': 'الخدمة',
  'apt.public.morning': 'الفترة الصباحية',
  'apt.public.afternoon': 'الفترة المسائية',
  'apt.public.noSlots': 'لا أوقات متاحة في هذا اليوم',
  'apt.public.fullName': 'الاسم الكامل',
  'apt.public.namePlaceholder': 'اكتب اسمك…',
  'apt.public.phone': 'رقم الجوال',
  'apt.public.phonePlaceholder': '05X XXX XXXX',
  'apt.public.consent': 'أوافق على {policy} وعلى التواصل معي عبر الرسائل بخصوص هذا الموعد.',
  'apt.public.privacyNotice': 'إشعار الخصوصية',
  'apt.public.submit': 'تأكيد طلب الحجز',
  'apt.public.submitting': 'جارٍ الإرسال…',
  'apt.public.reviewNote': 'طلبك سيخضع لمراجعة الاستقبال. سنرسل تأكيداً على جوالك.',
  'apt.public.autoNote': 'سيتم تأكيد موعدك فوراً وسنرسل تفاصيله على جوالك.',
  'apt.public.back': 'رجوع',
  'apt.public.continue': 'متابعة',
  'apt.public.nameRequired': 'الاسم مطلوب',
  'apt.public.phoneRequired': 'رقم الجوال مطلوب',
  'apt.public.consentRequired': 'يجب الموافقة على إشعار الخصوصية',
  'apt.public.slotRequired': 'اختر وقتاً متاحاً',
  'apt.public.rateLimited': 'محاولات كثيرة. يُرجى المحاولة بعد قليل.',
  'apt.public.slotTaken': 'حُجز هذا الوقت للتو. اختر وقتاً آخر.',
  'apt.public.closed': 'الحجز الإلكتروني غير متاح لهذه العيادة حالياً.',
  'apt.public.unavailable.HOLIDAY': 'إجازة رسمية',
  'apt.public.unavailable.DOCTOR_ON_LEAVE': 'الطبيب في إجازة',
  'apt.public.unavailable.NO_DOCTOR_SCHEDULE': 'لا دوام للطبيب',
  'apt.public.unavailable.BRANCH_CLOSED': 'الفرع مغلق',

  // --- Public confirmation -------------------------------------------------
  'apt.confirm.title': 'تم استلام طلبك',
  'apt.confirm.titleConfirmed': 'تم تأكيد موعدك',
  'apt.confirm.body': 'سيتواصل معك الاستقبال لتأكيد الموعد خلال ساعة عمل واحدة.',
  'apt.confirm.bodyConfirmed': 'موعدك مؤكد. يُرجى الحضور قبل الموعد بعشر دقائق.',
  'apt.confirm.reference': 'رقم المرجع',
  'apt.confirm.doctor': 'الطبيب',
  'apt.confirm.datetime': 'التاريخ والوقت',
  'apt.confirm.branch': 'الفرع',
  'apt.confirm.status': 'الحالة',
  'apt.confirm.policy':
    'سياسة الإلغاء: يُرجى الإلغاء قبل {hours} ساعات على الأقل. عدم الحضور دون إشعار قد يترتب عليه رسوم.',
  'apt.confirm.addToCalendar': 'إضافة إلى التقويم',
  'apt.confirm.bookAnother': 'حجز موعد آخر',
  'apt.confirm.keepReference': 'احتفظ برقم المرجع لمراجعة الاستقبال.',
};
